import * as next from '../types/next'

export const isRegularJourney = (
  journey?: next.Journey
): journey is next.RegularJourney => {
  return journey?.type === next.JOURNEY_TYPE.REGULAR
}

/**
 * @deprecated This type of check will not be needed with the new Journey Launcher architecture
 * @todo Remove when new architecture is fully deployed
 */
export const isLauncherJourney = (
  journey?: next.Journey
): journey is next.LauncherJourney => {
  return journey?.type === next.JOURNEY_TYPE.LAUNCHER
}

export const isPostQualificationJourney = (
  journey?: next.Journey
): journey is next.PostQualificationJourney => {
  return journey?.type === next.JOURNEY_TYPE.POST_QUALIFICATION
}

export const isTemplateJourney = (
  journey?: next.Journey
): journey is next.JourneyTemplate => {
  return journey?.isTemplate === true
}

export const isAllowedJourneyType = (
  journey?: next.Journey,
  /** Which types to exclude from allow list */
  exclude?: next.JOURNEY_TYPE[]
) => {
  return journey && allowedJourneyTypes(exclude).includes(journey.type)
}

export const allowedJourneyTypes = (
  /** Which types to exclude from allow list */
  exclude?: next.JOURNEY_TYPE[]
): next.JOURNEY_TYPE[] => {
  return Object.values(next.JOURNEY_TYPE).filter(
    (type) => exclude?.includes(type) == false
  )
}

export const isStackLayout = (
  layout?: next.StepLayout
): layout is next.StackLayout => {
  return layout?.type === next.LAYOUT_TYPE.STACK
}

export const isTwoColumnLayout = (
  layout?: next.StepLayout
): layout is next.TwoColumnLayout => {
  return layout?.type === next.LAYOUT_TYPE.TWO_COLUMN
}

export const isGridLayout = (
  layout?: next.StepLayout
): layout is next.GridLayout => {
  return layout?.type === next.LAYOUT_TYPE.GRID
}

export const generateEmptyStepLayout = (
  type: next.LAYOUT_TYPE
): next.StepLayout => {
  switch (type) {
    case next.LAYOUT_TYPE.STACK:
      return {
        type: next.LAYOUT_TYPE.STACK,
        content: { children: [] }
      }
    case next.LAYOUT_TYPE.TWO_COLUMN:
      return {
        type: next.LAYOUT_TYPE.TWO_COLUMN,
        header: { children: [] },
        content: { children: [] },
        sidebar: { children: [] },
        footer: { children: [] }
      }
    case next.LAYOUT_TYPE.GRID:
      return {
        type: next.LAYOUT_TYPE.GRID,
        content: { children: [] }
      }
    default:
      throw new Error(`Unsupported layout type: ${type}`)
  }
}

export const isGroupBlock = (block?: next.Block): block is next.GroupBlock => {
  return block?.type === next.BLOCK_TYPE.GROUP
}

/**
 * Find the layout zone of a given Block within a StepLayout.
 *
 * WARNING: If you're going to use this function multiple times for different blocks
 * that are part of the same Step, consider using {@link findLayoutZoneOfEachBlock} instead
 * for performance reasons.
 *
 * TODO: unit test
 */
export const findBlockLayoutZone = (
  stepLayout: next.StepLayout | undefined,
  blockId: next.BlockId
): next.LayoutZoneName | undefined => {
  if (!stepLayout) return undefined

  if (isStackLayout(stepLayout)) {
    if (stepLayout.content.children.includes(blockId)) {
      return 'content'
    }

    return undefined
  }

  if (isTwoColumnLayout(stepLayout)) {
    for (const zone of ['header', 'content', 'sidebar', 'footer'] as const) {
      if (stepLayout[zone].children.includes(blockId)) {
        return zone
      }
    }

    return undefined
  }

  throw new Error('Unsupported layout type')
}

/**
 * Constructs a map for all Blocks (by id) and their layout zones.
 *
 * This is useful when operating over many blocks of a given Step and preferred than
 * repeatedly traversing the layout structure for each block.
 *
 * TODO: unit test
 */
export const findLayoutZoneOfEachBlock = (
  layout: next.StepLayout | undefined
): Record<next.BlockId, next.LayoutZoneName> => {
  if (!layout) return {}

  if (isStackLayout(layout)) {
    return layout.content.children.reduce<
      ReturnType<typeof findLayoutZoneOfEachBlock>
    >((acc, blockId) => {
      acc[blockId] = 'content'

      return acc
    }, {})
  }

  if (isTwoColumnLayout(layout)) {
    const resultMap: Record<next.BlockId, next.LayoutZoneName> = {}

    for (const zone of ['header', 'content', 'sidebar', 'footer'] as const) {
      for (const blockId of layout[zone].children) {
        resultMap[blockId] = zone
      }
    }

    return resultMap
  }

  throw new Error('Unsupported layout type')
}
